import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchHouseDetails } from '../api/houseApi';
import { useHouseStore } from '../store/houseStore';
import { saveImageToDB } from '../db/imageDb';
import { House } from '../types/house';
import { Search, Loader2, AlertCircle } from 'lucide-react';

const AddHouse: React.FC = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const addHouse = useHouseStore((state) => state.addHouse);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    setLoading(true);
    setError(null);

    try {
      const details = await fetchHouseDetails(url);
      
      let imageId: string | undefined = undefined;
      if (details.imageUrl) {
        try {
          const imgRes = await fetch(details.imageUrl);
          if (imgRes.ok) {
            const blob = await imgRes.blob();
            imageId = await saveImageToDB(blob);
          }
        } catch (e) {
          console.error('Failed to download and save image', e);
        }
      }

      const newHouse: House = {
        id: crypto.randomUUID(),
        url: url,
        address: details.address || 'New Property',
        priceRangeLow: details.priceRangeLow || 0,
        priceRangeHigh: details.priceRangeHigh || 0,
        estimate: details.estimate || 0,
        bedrooms: details.bedrooms || 0,
        bathrooms: details.bathrooms || 0,
        parking: details.parking || 0,
        propertySize: details.propertySize || 0, // Building size
        landSize: details.landSize || 0,
        sourceUrl: details.sourceUrl || url,
        imageUrl: details.imageUrl || '',
        imageId: imageId,
        notes: '',
        myPricePrediction: 0,
        feel: 3,
        
        // Extended Details
        displayPrice: details.displayPrice,
        capitalValue: details.capitalValue,
        improvementValue: details.improvementValue,
        landValue: details.landValue,
        rentalAppraisalLow: details.rentalAppraisalLow,
        rentalAppraisalHigh: details.rentalAppraisalHigh,
        rentalYield: details.rentalYield,
        legalDescription: details.legalDescription,
        ownershipType: details.ownershipType,
        certificateOfTitle: details.certificateOfTitle,
        decadeBuilt: details.decadeBuilt,
        buildingConstruction: details.buildingConstruction,
        contour: details.contour,
        siteCoverage: details.siteCoverage,
        hasDeck: details.hasDeck,
        hasLaundry: details.hasLaundry,
        viewType: details.viewType,
        street: details.street,
        suburb: details.suburb,
        city: details.city,
      };

      addHouse(newHouse);
      navigate(`/edit/${newHouse.id}`); // Go to edit page to refine details
    } catch (err) {
      console.error(err);
      setError('Failed to fetch house details. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow sm:rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Property</h2>
        <p className="text-gray-600 mb-6">
          Enter the URL of the property listing. We'll automatically fetch the details for you.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Property URL
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                name="url"
                id="url"
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md border p-2.5"
                placeholder="https://homes.co.nz/address/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              loading ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Fetching Details...
              </>
            ) : (
              'Fetch & Add Property'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddHouse;
